/**
 * kd-collection-filter のビジネスロジック（純粋関数）
 */

/** 価格スライダーの UI 上限値。この値ちょうどは「上限なし」として扱う */
export const PRICE_SLIDER_MAX = 50000;

/**
 * min / max から Algolia price filter 式を生成する。
 * min=0 かつ max===PRICE_SLIDER_MAX（かつユーザー明示入力でない）はフィルタなし。
 * maxExplicit=true の場合、max===PRICE_SLIDER_MAX でも上限フィルタを付与する。
 *
 * 両端指定は範囲構文 `price:min TO max`（単一 filter 扱い、両端 inclusive）で表現する。
 * Algolia の filters 構文は「AND と OR の混在」を許可しない（`(A AND B) OR (C AND D)` は 400）。
 * 価格 preset / 自由入力は group='price' で OR 結合される（案A / 課題#16）ため、
 * `price >= min AND price <= max` の複合 AND 式では複数選択時に無効なフィルタになる。
 * 片端のみの指定は比較演算（これも単一 filter なので OR 可）。
 */
export function buildPriceRangeFilterExpr(min: number, max: number, maxExplicit = false): string {
  const hasMin = min > 0;
  const hasMax = max !== PRICE_SLIDER_MAX || maxExplicit;
  if (!hasMin && !hasMax) return '';
  if (hasMin && hasMax) return `price:${min} TO ${max}`;
  if (hasMin) return `price >= ${min}`;
  return `price <= ${max}`;
}

/** テキスト入力の文字列（カンマ区切り等含む）を数値にパースする。失敗時は fallback を返す。 */
export function parsePriceInputValue(raw: string, fallback: number): number {
  const num = Number(raw.replace(/[^\d]/g, ''));
  return Number.isFinite(num) && num >= 0 ? num : fallback;
}

/**
 * SP 絞り込みトリガーの「適用中フィルタ数」バッジ用カウント。
 * チェック中の Algolia filter 式（重複除外）＋価格レンジ（あれば 1）を数える。
 * dispatchFilterApply が送る filters 集合と同じ数え方（expr で dedup）に揃える。
 *
 * @param filterExprs チェック中 checkbox の data-algolia-filter 値の配列（空/重複を含んでよい）
 * @param hasPriceRange 自由入力の価格レンジフィルタが有効か
 */
export function countActiveFilters(filterExprs: string[], hasPriceRange: boolean): number {
  const unique = new Set(filterExprs.filter((expr) => expr && expr.length > 0));
  return unique.size + (hasPriceRange ? 1 : 0);
}

/**
 * 大カテゴリ（lvl0）と中カテゴリ（lvl1）の同時選択を防ぐための、
 * 「いまチェックしたものと排他になる filter-id」を求める（課題管理表 #68）。
 *
 * 絞り込みパネルは大カテゴリ行に filter_group='hierarchicalCategories' を渡しており、
 * lvl0 と lvl1 が同一 OR グループに入る（snippets/v2-search-filter-sections.liquid）。
 * lvl1 を持つ商品は必ず lvl0 も持つため、両方チェックすると
 * `lvl0:"食品" OR lvl1:"食品 > 肉類"` の lvl0 側に吸収され、中カテゴリで絞り込めない。
 * 同じ absorption は中カテゴリ collection ページ側では回避済み（search-main.logic.ts 参照）。
 *
 * 起票者確認（2026-08-21）で「大カテゴリ側のチェックを自動で外す」を採択。
 * 逆向き（親を選んだら配下の子を外す）も対称に行う。対称にしないと
 * 「親をクリックしても子が残って結果が変わらない」状態になる。
 *
 * 🔴 別の大カテゴリは対象外。「食品」＋「お菓子 > チョコレート」は
 * 食品全体とチョコレートの両方を出すのが正（起票者確認済み）。
 * そのため親子の判定は id の前方一致のみで行い、他の大カテゴリには触らない。
 *
 * id の規約: 子は `<親id>-child-<連番>`（snippets/v2-search-filter-category-row.liquid）。
 * `sp-` 接頭辞は呼び出し側で除去してから渡す。
 *
 * @param checkedBaseId いまチェックされた checkbox の filter-id（`sp-` 除去済み）
 * @param allBaseIds パネル内の全 filter-id（`sp-` 除去済み・重複可）
 * @returns チェックを外すべき filter-id（`sp-` なし）。対象外なら空配列
 */
export function getExclusiveCategoryIds(checkedBaseId: string, allBaseIds: string[]): string[] {
  if (!checkedBaseId) return [];

  // 子がチェックされた → その親だけを外す
  const asChild = checkedBaseId.match(/^(.+)-child-\d+$/);
  if (asChild) {
    const parentId = asChild[1];
    return allBaseIds.includes(parentId) ? [parentId] : [];
  }

  // 親がチェックされた → その配下の子をすべて外す
  const childPrefix = `${checkedBaseId}-child-`;
  const children = allBaseIds.filter((id) => id.startsWith(childPrefix));
  return Array.from(new Set(children));
}
